/**
 * El vocabulario de íconos del producto (ADR-016).
 *
 * **Una sola librería y un solo peso.** Todo ícono de interfaz sale de
 * `lucide-react` —lineal, trazo medio— y la jerarquía se hace con **tamaño y
 * token de color**, nunca mezclando rellenos y lineales. Antes de este módulo el
 * producto dibujaba sus íconos con glifos y emoji sueltos en cada pantalla, que
 * es la inconsistencia que el ADR cierra.
 *
 * **Por qué re-exportar en vez de importar de `lucide-react` en cada pantalla.**
 * Para que el vocabulario esté en un archivo y no repartido: acá se ve, de un
 * vistazo, qué íconos usa el producto y con qué nombre de dominio. Si hace falta
 * uno que no está, se agrega acá; si no existe en la librería, se decide por ADR
 * y no se dibuja de paso. Los re-exports nombrados no rompen el tree-shaking:
 * sólo se empaqueta el ícono que alguna pantalla realmente usa.
 *
 * **Accesibilidad.** Los componentes de Lucide ya marcan el `svg` con
 * `aria-hidden` salvo que reciban una prop de accesibilidad, así que el ícono es
 * decorativo por defecto. Cuando el ícono es **el único contenido de un
 * control**, el nombre accesible va en el control (`aria-label` del botón o del
 * enlace), no en el ícono: es lo que anuncia un lector de pantalla y lo que pide
 * WCAG 2.1 AA (4.1.2).
 */

/** El tipo de cualquier ícono del vocabulario, para las props que reciben uno. */
export type { LucideIcon as Icono } from 'lucide-react';
export {
  // Navegación y estructura
  Activity,
  Angry,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Bandage,
  Bell,
  BellOff,
  BookOpen,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CircleCheck,
  ClipboardList,
  Clock,
  CloudUpload,
  DoorOpen,
  Download,
  Eye,
  EyeOff,
  FileSpreadsheet,
  FileText,
  Footprints,
  Frown,
  GitBranch,
  House,
  Info,
  Laugh,
  LayoutDashboard,
  Lock,
  LogOut,
  Meh,
  Menu,
  MessageCircle,
  MessageCircleOff,
  MessageSquareWarning,
  Minus,
  PersonStanding,
  Phone,
  PhoneOff,
  Pill,
  Plus,
  Printer,
  Quote,
  Search,
  Send,
  Server,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Smile,
  Stethoscope,
  TriangleAlert,
  UserPlus,
  UserRound,
  Users,
  Video,
  WifiOff,
  X,
  Zap,
} from 'lucide-react';

/**
 * Los tamaños del sistema, como clases de Tailwind.
 *
 * Son cuatro y no más: la jerarquía se lee cuando los escalones son pocos y
 * distinguibles. `size-*` se aplica por CSS y gana sobre los atributos `width` y
 * `height` que Lucide escribe en el `svg`, así que el tamaño sale de acá y no de
 * la prop `size` de cada llamada.
 */
export const ICONO = Object.freeze({
  /** Denso: tablas y listas del workspace. */
  chico: 'size-4',
  /** Inline junto a texto. */
  normal: 'size-5',
  /** Tab bar y controles del paciente. */
  grande: 'size-7',
  /** Tarjetas de elección grandes y confirmaciones. */
  destacado: 'size-8',
});
